/**
 * 公开事件流（GET /public/world/feed）、公开图层的 move → 星球上的「刚发生了事」高亮和途中弧线。纯函数，页面（6c2b 观察室）调用。
 * - 高亮的时刻用「页面第一次看到这一条」的时刻，不用事件本身的 at：球只闪离现在 10 秒内的（globeStage），
 *   而轮询拿到的事件通常早过了 10 秒，按 at 算就一条也不闪。
 * - 第一次打开页面已有的那些、往前翻历史翻出来的，都只记为见过，不闪：闪的只是「看着的时候新发生的」。
 * - 只用 area 的格子中心（只到片区，与图层同一 0.01° 网格）；area 为 null 的（相遇恒为 null、说不清的）不闪，不替它编位置。
 * - 事件本身太旧的（比这一页的 server_time 早过 maxAgeMs）也不闪：防止游标回退、补登时把旧事当新事。
 */
import type { PublicMapResident, PublicWorldFeed } from "@/shared/contracts/generated";

import type { GlobeHighlight, GlobeRoute } from "./globeModel";

/** 默认只闪事件时刻离这一页 server_time 不超过 10 分钟的 */
export const FEED_FLASH_MAX_AGE_MS = 10 * 60 * 1000;

export interface FeedFlashInput {
  /** 之前已经见过的 event_id（页面自己存，跨轮询保留） */
  seen: ReadonlySet<string>;
  /** 这一次拿到的一页 */
  page: PublicWorldFeed;
  /** 页面拿到这一页的本机时刻（毫秒） */
  receivedAt: number;
  /** 第一次打开、或往前翻历史（带 before 游标）拿到的页：只记见过，不闪 */
  baseline: boolean;
  maxAgeMs?: number;
}

export interface FeedFlashResult {
  highlights: GlobeHighlight[];
  /** 加上这一页之后的已见集合（新对象，不改传进来的那个） */
  seen: Set<string>;
}

export function feedHighlights({ seen, page, receivedAt, baseline, maxAgeMs = FEED_FLASH_MAX_AGE_MS }: FeedFlashInput): FeedFlashResult {
  const next = new Set(seen);
  const highlights: GlobeHighlight[] = [];
  const serverNow = Date.parse(page.server_time);
  for (const item of page.items) {
    if (next.has(item.event_id)) continue;
    next.add(item.event_id);
    if (baseline || !item.area) continue;
    const at = Date.parse(item.at);
    if (!Number.isFinite(at) || !Number.isFinite(serverNow) || serverNow - at > maxAgeMs) continue;
    const { lat, lng } = item.area;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    highlights.push({ key: item.event_id, lat, lng, at: receivedAt });
  }
  return { highlights, seen: next };
}

/**
 * 在途居民的弧线：只画有 move 的（在驿站、到访中 move 为 null），两头都是片区格子中心。
 * 两头落在同一格的不画（弧长为零，画出来只是一个点，会被当成光点）。key 用宠物号：同一位一次只有一条。
 */
export function moveRoutes(residents: readonly PublicMapResident[]): GlobeRoute[] {
  const routes: GlobeRoute[] = [];
  for (const r of residents) {
    const move = r.move;
    if (!move) continue;
    const from = { lat: move.from_area.lat, lng: move.from_area.lng };
    const to = { lat: move.to_area.lat, lng: move.to_area.lng };
    if (![from.lat, from.lng, to.lat, to.lng].every(Number.isFinite)) continue;
    if (from.lat === to.lat && from.lng === to.lng) continue;
    routes.push({ key: r.pet_id, from, to });
  }
  return routes;
}
